import React, { useRef, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Text,
  Box,
  VStack,
  Icon,
  Tag,
  TagLabel,
  TagCloseButton,
  useToast,
} from "@chakra-ui/react";
import { FaCloudUploadAlt } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import { importPoolMembersFromFile } from "../../../api/partnersApi";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX,
} from "../../../components/Portal/portalModalChrome";

const ImportPoolMembersFromFileModal = ({ isOpen, onClose, poolId, onSuccess }) => {
  const { selectedBusinessUnitId } = useBusinessUnit();
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const toast = useToast();
  const { t } = useTranslation("portal");

  const buildImportSummary = (payload) => {
    const total = payload?.totalRows;
    const added = payload?.addedRows;
    const updated = payload?.updatedRows;
    const deleted = payload?.deletedRows;
    const duplicate = payload?.duplicateRows;
    const parts = [];
    if (total != null)
      parts.push(`${t("partners.pools.members.importFromFile.statusLabels.total")}: ${total}`);
    if (added != null)
      parts.push(`${t("partners.pools.members.importFromFile.statusLabels.added")}: ${added}`);
    if (updated != null)
      parts.push(`${t("partners.pools.members.importFromFile.statusLabels.updated")}: ${updated}`);
    if (deleted != null)
      parts.push(`${t("partners.pools.members.importFromFile.statusLabels.deleted")}: ${deleted}`);
    if (duplicate != null)
      parts.push(`${t("partners.pools.members.importFromFile.statusLabels.duplicate")}: ${duplicate}`);
    return parts.length ? parts.join(" · ") : "";
  };

  const isSupportedFile = (f) => {
    if (!f) return false;
    const mime = f.type;
    const name = (f.name || "").toLowerCase();
    if (
      mime === "text/csv" ||
      mime === "application/vnd.ms-excel" ||
      mime === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      return true;
    }
    if (name.endsWith(".csv") || name.endsWith(".xlsx")) {
      return true;
    }
    return false;
  };

  const handleFilesSelected = (event) => {
    const selected = event.target.files?.[0] || null;
    if (selected && isSupportedFile(selected)) {
      setFile(selected);
    } else if (selected) {
      toast({
        title: t("partners.pools.members.importFromFile.toasts.invalidFileTypeTitle"),
        description: t(
          "partners.pools.members.importFromFile.toasts.invalidFileTypeDescription"
        ),
        status: "error",
      });
    }
    event.target.value = null;
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const f = e.dataTransfer.files[0];
      if (isSupportedFile(f)) {
        setFile(f);
      } else {
        toast({
          title: t("partners.pools.members.importFromFile.toasts.invalidFileTypeTitle"),
          description: t(
            "partners.pools.members.importFromFile.toasts.invalidFileTypeDescription"
          ),
          status: "error",
        });
      }
    }
  };

  const handleClose = () => {
    setFile(null);
    setDragActive(false);
    setIsSubmitting(false);
    onClose?.();
  };

  const handleSubmit = async () => {
    if (!file || !poolId) return;
    setIsSubmitting(true);
    try {
      const res = await importPoolMembersFromFile(poolId, file, {
        buId: selectedBusinessUnitId || undefined,
      });
      const summary = buildImportSummary(res);
      toast({
        title: t("partners.pools.members.importFromFile.toasts.importCompleted"),
        description: summary || undefined,
        status: "success",
      });
      onSuccess?.(res);
      handleClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const isTooLarge = e?.response?.status === 400 && detail === "IMPORT_TOO_LARGE";
      const isUnsupported =
        e?.response?.status === 400 && detail === "UNSUPPORTED_FILE_TYPE";
      const isInvalidExcel =
        e?.response?.status === 400 && detail === "INVALID_EXCEL_FILE";

      if (isTooLarge) {
        toast({
          title: t("partners.pools.members.importFromFile.toasts.importTooLargeTitle"),
          description: t(
            "partners.pools.members.importFromFile.toasts.importTooLargeDescription"
          ),
          status: "error",
        });
      } else if (isUnsupported) {
        toast({
          title: t("partners.pools.members.importFromFile.toasts.invalidFileTypeTitle"),
          description: t(
            "partners.pools.members.importFromFile.toasts.invalidFileTypeDescription"
          ),
          status: "error",
        });
      } else if (isInvalidExcel) {
        toast({
          title: t("partners.pools.members.importFromFile.toasts.importFailedTitle"),
          description: t(
            "partners.pools.members.importFromFile.toasts.invalidExcelDescription"
          ),
          status: "error",
        });
      } else {
        toast({
          title: t("partners.pools.members.importFromFile.toasts.importFailedTitle"),
          description:
            e?.response?.data?.message ||
            t("partners.pools.members.importFromFile.toasts.importFailedDescription"),
          status: "error",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay bg="rgba(11,31,58,.45)" />
      <ModalContent sx={MODAL_CONTENT_SX}>
        <ModalHeader sx={MODAL_HEADER_SX} data-testid="import-pool-members-modal-title">
          {t("partners.pools.members.importFromFile.modalTitle")}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" mb={4} sx={{ marginBottom: "16px !important" }}>
            {t("partners.pools.members.importFromFile.description")}
          </Text>

          <FormControl isRequired>
            <FormLabel>
              {t("partners.pools.members.importFromFile.fields.file")}
            </FormLabel>
            {file ? (
              <Tag
                size="md"
                variant="solid"
                colorScheme="blue"
                data-testid="import-pool-members-selected-file"
              >
                <TagLabel>{file.name}</TagLabel>
                <TagCloseButton onClick={() => setFile(null)} />
              </Tag>
            ) : (
              <Box
                data-testid="import-pool-members-dropzone"
                border="2px dashed"
                borderColor={dragActive ? "portal.primary" : "portal.line"}
                borderRadius="md"
                p={6}
                textAlign="center"
                minH="160px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                bg={dragActive ? "portal.active" : "transparent"}
                transition="all 0.2s"
                cursor="pointer"
                onClick={() => fileInputRef.current?.click()}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                _hover={{ bg: "portal.soft" }}
              >
                <VStack spacing={2}>
                  <Icon as={FaCloudUploadAlt} boxSize={8} color="portal.muted" />
                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    sx={{ marginBottom: "0 !important" }}
                  >
                    {t("partners.pools.members.importFromFile.dropzone.text")}
                  </Text>
                  <Text
                    fontSize="xs"
                    color="portal.muted"
                    sx={{ marginBottom: "0 !important" }}
                  >
                    {t("partners.pools.members.importFromFile.dropzone.accepted")}
                  </Text>
                </VStack>
                <Input
                  ref={fileInputRef}
                  data-testid="import-pool-members-file-input"
                  type="file"
                  accept=".csv,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={handleFilesSelected}
                  display="none"
                />
              </Box>
            )}
          </FormControl>
        </ModalBody>
        <ModalFooter sx={MODAL_FOOTER_SX}>
          <Button
            data-testid="import-pool-members-cancel"
            variant="secondary"
            size="sm"
            mr={3}
            onClick={handleClose}
          >
            {t("partners.pools.members.importFromFile.buttons.cancel")}
          </Button>
          <Button
            data-testid="import-pool-members-submit"
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            isDisabled={!file}
            isLoading={isSubmitting}
          >
            {t("partners.pools.members.importFromFile.buttons.import")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ImportPoolMembersFromFileModal;
